import * as React from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'
import { useIntersectionObserver } from 'hooks/use-intersection-observer'
import type { Product } from 'cf-grpc/creativefabrica/content/v2/types_pb'
import { marketplaceListProductsQueryFetcher } from '@web/gen/content'
import { MarketplaceLanguage, MarketplaceProductSortOrder } from '@web/gen/types'
import type { Language } from 'codegen/types'

import { mapMarketplaceProductToLegacyProduct } from '../../_components/marketplace-page/marketplace'

interface PageParam {
  page: number
}

interface InfinitePageData {
  products: Product[]
  pagination: {
    currentPage: number
    totalPages: number
  }
}

function getTargetPage(): number {
  if (typeof window === 'undefined') {
    return 1
  }
  const param = new URLSearchParams(window.location.search).get('page')
  const page = Number(param)
  return !Number.isNaN(page) && page >= 2 ? page : 1
}

function mapLocaleToMarketplaceLanguage(locale: Language): MarketplaceLanguage {
  switch (locale) {
    case 'de': {
      return MarketplaceLanguage.LanguageDe
    }
    case 'es': {
      return MarketplaceLanguage.LanguageEs
    }
    case 'fr': {
      return MarketplaceLanguage.LanguageFr
    }
    case 'it': {
      return MarketplaceLanguage.LanguageIt
    }
    case 'nl': {
      return MarketplaceLanguage.LanguageNl
    }
    case 'pl': {
      return MarketplaceLanguage.LanguagePl
    }
    case 'pt': {
      return MarketplaceLanguage.LanguagePt
    }
    default: {
      return MarketplaceLanguage.LanguageEn
    }
  }
}

function mapSortOrder(orderBy: string): MarketplaceProductSortOrder {
  if (orderBy === 'date') {
    return MarketplaceProductSortOrder.ProductSortOrderMostRecent
  }
  return MarketplaceProductSortOrder.ProductSortOrderMostPopular
}

async function fetchMarketplacePage(params: {
  slug: string
  orderBy: string
  limit: number
  page: number
  locale: Language
}): Promise<InfinitePageData> {
  const fetcher = marketplaceListProductsQueryFetcher({
    input: {
      categorySlug: params.slug,
      locale: mapLocaleToMarketplaceLanguage(params.locale),
      pagination: {
        currentPage: params.page,
        pageSize: params.limit,
      },
      sortType: mapSortOrder(params.orderBy),
    },
  })

  const response = await fetcher()
  const payload = response?.marketplaceListProducts
  const rawProducts = payload?.products ?? []
  const products = rawProducts.map(product =>
    mapMarketplaceProductToLegacyProduct(product, params.locale),
  )
  const totalCount = payload?.pagination?.totalCount ?? 0
  const totalPages = params.limit > 0 ? Math.ceil(totalCount / params.limit) : 0

  return {
    products,
    pagination: {
      currentPage: params.page,
      totalPages,
    },
  }
}

export function useInfiniteLoadingProducts({
  slug,
  orderBy,
  limit,
  locale,
}: {
  slug: string
  orderBy: string
  limit: number
  locale: Language
}) {
  const [ref, entry] = useIntersectionObserver<HTMLDivElement>()
  const targetPage = React.useMemo(getTargetPage, [])

  const [enabled, setEnabled] = React.useState(false)
  const [hydrated, setHydrated] = React.useState(targetPage < 2)

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<
    InfinitePageData,
    Error,
    InfiniteData<InfinitePageData, PageParam>,
    readonly unknown[],
    PageParam
  >({
    queryKey: ['bundles-marketplace-products', slug, orderBy, limit, locale],
    enabled,
    initialPageParam: { page: 2 },
    queryFn: ({ pageParam }) =>
      fetchMarketplacePage({ slug, orderBy, limit, page: pageParam.page, locale }),
    getNextPageParam: lastPage => {
      const { currentPage, totalPages } = lastPage.pagination
      if (!currentPage || !totalPages || currentPage >= totalPages) {
        return
      }
      return { page: currentPage + 1 }
    },
  })

  React.useEffect(() => {
    if (targetPage < 2) {
      return
    }
    if (!enabled) {
      setEnabled(true)
    }
  }, [targetPage, enabled])

  React.useEffect(() => {
    if (hydrated) {
      return
    }
    if (!enabled || !hasNextPage || isFetchingNextPage) {
      return
    }

    fetchNextPage().then(result => {
      const lastPage = result.data?.pages?.at(-1)
      const currentPage = lastPage?.pagination?.currentPage ?? 2
      if (currentPage >= targetPage) {
        setHydrated(true)
      }
    })
  }, [hydrated, enabled, targetPage, hasNextPage, isFetchingNextPage, fetchNextPage])

  React.useEffect(() => {
    if (!hydrated) {
      return
    }
    if (!entry?.isIntersecting) {
      return
    }

    let timeoutId: NodeJS.Timeout | null = null

    if (!enabled) {
      setEnabled(true)
      return
    }

    if (!hasNextPage || isFetchingNextPage) {
      return
    }

    timeoutId = setTimeout(() => {
      fetchNextPage()
    }, 300)

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [hydrated, entry?.isIntersecting, enabled, hasNextPage, isFetchingNextPage, fetchNextPage])

  React.useEffect(() => {
    const lastPage = data?.pages?.at(-1)
    const currentPage = lastPage?.pagination?.currentPage
    if (!currentPage) {
      return
    }

    const url = new URL(window.location.href)
    if (currentPage >= 2) {
      url.searchParams.set('page', String(currentPage))
    } else {
      url.searchParams.delete('page')
    }
    window.history.replaceState({}, '', url.toString())
  }, [data])

  const products = data?.pages?.flatMap(page => page.products ?? []) ?? []
  return { ref, products, isFetchingNextPage }
}
